/**
 * 鱼缸创建表单组件
 * 用于在评估页面 Modal 内创建新鱼缸
 *
 * @see Requirements 5.2, 5.3
 */

import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import tankService, {
  Tank,
  FilterSystemOption,
  WaterTypeOption,
} from "@/services/tankService";

// 表单验证 Schema
const tankFormSchema = z.object({
  name: z.string().min(1, "请输入鱼缸名称").max(50, "名称不能超过50个字符"),
  length: z
    .number({ invalid_type_error: "请输入长度" })
    .min(1, "长度至少为1cm")
    .max(999, "长度不能超过999cm"),
  width: z
    .number({ invalid_type_error: "请输入宽度" })
    .min(1, "宽度至少为1cm")
    .max(999, "宽度不能超过999cm"),
  height: z
    .number({ invalid_type_error: "请输入高度" })
    .min(1, "高度至少为1cm")
    .max(999, "高度不能超过999cm"),
  water_type: z.string().min(1, "请选择水体类型"),
  filter_system: z.string().optional(),
});

type TankFormValues = z.infer<typeof tankFormSchema>;

export interface TankCreationFormProps {
  /** 创建成功回调 */
  onSuccess: (tank: Tank) => void;
  /** 取消回调（返回鱼缸列表） */
  onCancel?: () => void;
  /** 跳过回调（不创建鱼缸直接开始评估） */
  onSkip?: () => void;
  /** 过滤系统选项 */
  filterSystemOptions: FilterSystemOption[];
  /** 水体类型选项 */
  waterTypeOptions: WaterTypeOption[];
}

/**
 * 鱼缸创建表单组件
 * @see Requirements 5.2 - 显示创建鱼缸所需的必填字段
 * @see Requirements 5.3 - 提交有效数据后创建鱼缸
 * @see Requirements 5.6 - 跳过创建
 */
const TankCreationForm: React.FC<TankCreationFormProps> = ({
  onSuccess,
  onCancel,
  onSkip,
  filterSystemOptions,
  waterTypeOptions,
}) => {
  const { t } = useTranslation("assessments");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const form = useForm<TankFormValues>({
    resolver: zodResolver(tankFormSchema),
    defaultValues: {
      name: "",
      length: undefined,
      width: undefined,
      height: undefined,
      water_type: "",
      filter_system: "",
    },
  });

  // 计算预计容积
  const watchedValues = form.watch(["length", "width", "height"]);
  const volumeL = React.useMemo(() => {
    const [length, width, height] = watchedValues;
    if (length && width && height && length > 0 && width > 0 && height > 0) {
      return Math.round((length * width * height) / 1000);
    }
    return null;
  }, [watchedValues]);

  /**
   * 提交表单
   * @see Requirements 5.3
   */
  const onSubmit = async (data: TankFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await tankService.createTank({
        name: data.name,
        length: data.length,
        width: data.width,
        height: data.height,
        water_type: data.water_type,
        filter_system: data.filter_system || undefined,
      });

      onSuccess(response.tank);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "创建鱼缸失败，请重试";
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* 鱼缸名称 */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("tankCreation.name", "鱼缸名称")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t(
                    "tankCreation.namePlaceholder",
                    "给你的鱼缸起个名字",
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* 尺寸 */}
        <div className="space-y-2">
          <FormLabel>{t("tankCreation.dimensions", "鱼缸尺寸 (cm)")}</FormLabel>
          <div className="grid grid-cols-3 gap-3">
            <FormField
              control={form.control}
              name="length"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="numeric"
                        placeholder="0"
                        className="pr-10"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value ? Number(value) : undefined);
                        }}
                        value={field.value ?? ""}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        {t("tankCreation.length", "长")}
                      </span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="width"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="numeric"
                        placeholder="0"
                        className="pr-10"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value ? Number(value) : undefined);
                        }}
                        value={field.value ?? ""}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        {t("tankCreation.width", "宽")}
                      </span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="height"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="numeric"
                        placeholder="0"
                        className="pr-10"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value ? Number(value) : undefined);
                        }}
                        value={field.value ?? ""}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        {t("tankCreation.height", "高")}
                      </span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {volumeL && (
            <p className="text-sm text-muted-foreground">
              {t("tankCreation.estimatedVolume", "预计容积约")}{" "}
              <span className="font-medium text-foreground">{volumeL} L</span>
            </p>
          )}
        </div>

        {/* 水体类型 */}
        <FormField
          control={form.control}
          name="water_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("tankCreation.waterType", "水体类型")}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "tankCreation.waterTypePlaceholder",
                        "选择水体类型",
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {waterTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* 过滤系统（可选） */}
        <FormField
          control={form.control}
          name="filter_system"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("tankCreation.filterSystem", "过滤系统")}
                <span className="text-muted-foreground ml-1">
                  ({t("tankCreation.optional", "可选")})
                </span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "tankCreation.filterSystemPlaceholder",
                        "选择过滤系统",
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {filterSystemOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* 错误提示 */}
        {submitError && (
          <div className="p-3 bg-destructive/10 rounded-lg text-sm text-destructive">
            {submitError}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-3 pt-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t("tankCreation.cancel", "返回")}
            </Button>
          )}
          <Button
            type="submit"
            disabled={isSubmitting}
            className={onCancel ? "flex-1" : "w-full"}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("tankCreation.creating", "创建中...")}
              </>
            ) : (
              t("tankCreation.create", "创建鱼缸")
            )}
          </Button>
        </div>

        {/* 跳过按钮 */}
        {onSkip && (
          <div className="pt-2 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={onSkip}
              disabled={isSubmitting}
              className="w-full text-muted-foreground"
            >
              {t("tankCreation.skip", "跳过，直接开始评估")}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

export default TankCreationForm;
